import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import MainLayout from "layouts/Main";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Button,
  IconButton,
  Modal,
  Checkbox,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { grey } from "@mui/material/colors";
import { useAppDispatch, useAppSelector } from "hooks";
import { fetchBatteries, fetchBatteryGroups } from "store/appSlice";
import { selectBatteries, selectBatteryGroups } from "store/selectors";
import {
  getBatteryForecasts,
  savePlannedTransactions,
} from "api/resources/batteries";
import { BatteryForecast } from "api/models/Battery";
import BatteryForecastHistoryModal from "./BatteryForecastHistoryModal";

dayjs.extend(utc);
dayjs.extend(timezone);

const Batteries = () => {
  const dispatch = useAppDispatch();
  const batteries = useAppSelector(selectBatteries);
  const batteryGroups = useAppSelector(selectBatteryGroups);
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL parameters
  const [selectedBattery, setSelectedBattery] = useState<number | null>(() => {
    const param = searchParams.get("batteryId");
    return param ? parseInt(param, 10) : null;
  });
  const [selectedBatteryGroup, setSelectedBatteryGroup] = useState<
    number | null
  >(() => {
    const param = searchParams.get("batteryGroupId");
    return param ? parseInt(param, 10) : null;
  });
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(() => {
    const param = searchParams.get("date");
    return param ? dayjs(param) : dayjs();
  });
  const [forecasts, setForecasts] = useState<BatteryForecast[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState<
    Record<
      number,
      {
        plannedTransaction?: string;
        plannedTransactionPrice?: string;
        plannedTransactionIsExecuted?: boolean;
      }
    >
  >({});
  const [snackbar, setSnackbar] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [historyModal, setHistoryModal] = useState(false);
  const [historyForecastId, setHistoryForecastId] = useState<number | null>(
    null,
  );
  const [historyTimeLabel, setHistoryTimeLabel] = useState("");
  const [errorFields, setErrorFields] = useState<Record<number, Set<string>>>(
    {},
  );

  const FIELD_LABELS: Record<string, string> = {
    plannedTransaction: "Planned transaction",
    plannedTransactionPrice: "Planned price",
  };

  const MFRR_STATUS_COLORS: Record<string, string> = {
    WAITING: "#f9a825", // amber
    ACTIVATION: "#1976d2", // blue
    SUCCESS: "#2e7d32", // green
    END: grey[400], // light grey
  };

  const snackbarClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar(null);
  };

  // Update URL parameters when filters change
  const updateUrlParams = useCallback(
    (
      date: Dayjs | null,
      batteryId: number | null,
      batteryGroupId: number | null,
    ) => {
      const params = new URLSearchParams();
      if (date) {
        params.set("date", date.format("YYYY-MM-DD"));
      }
      if (batteryId) {
        params.set("batteryId", String(batteryId));
      }
      if (batteryGroupId) {
        params.set("batteryGroupId", String(batteryGroupId));
      }
      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    dispatch(fetchBatteries());
    dispatch(fetchBatteryGroups());
  }, [dispatch]);

  const fetchForecasts = useCallback(
    async (silent = false) => {
      if (!selectedDate || (!selectedBattery && !selectedBatteryGroup)) {
        setForecasts([]);
        return;
      }

      if (!silent) {
        setLoading(true);
      }
      try {
        const response = await getBatteryForecasts({
          date: selectedDate.format("YYYY-MM-DD"),
          batteryId: selectedBattery || undefined,
          batteryGroupId: selectedBatteryGroup || undefined,
        });

        setForecasts(response.data.forecasts || []);
        setChanges({});
      } catch (error) {
        console.error("Error fetching battery forecasts:", error);
        if (!silent) {
          setForecasts([]);
        }
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [selectedDate, selectedBattery, selectedBatteryGroup],
  );

  useEffect(() => {
    fetchForecasts();
  }, [fetchForecasts]);

  const handleInputChange = (
    forecastId: number,
    field: "plannedTransaction" | "plannedTransactionPrice",
    value: string,
  ) => {
    setChanges((prev) => ({
      ...prev,
      [forecastId]: {
        ...prev[forecastId],
        [field]: value,
      },
    }));
    setErrorFields((prev) => {
      if (!prev[forecastId]?.has(field)) return prev;
      const next = { ...prev };
      const updated = new Set(next[forecastId]);
      updated.delete(field);
      if (updated.size === 0) {
        delete next[forecastId];
      } else {
        next[forecastId] = updated;
      }
      return next;
    });
  };

  const handleIsExecutedChange = (forecastId: number, value: boolean) => {
    setChanges((prev) => ({
      ...prev,
      [forecastId]: {
        ...prev[forecastId],
        plannedTransactionIsExecuted: value,
      },
    }));
  };

  // Get the display name for the selected battery or battery group
  const selectedDisplayName = useMemo(() => {
    if (selectedBattery) {
      const battery = batteries?.find((b) => b.id === selectedBattery);
      return (
        `${battery?.productionParkName} - ${battery?.id} (${battery?.capacity}kWh)` ||
        "Battery"
      );
    }
    if (selectedBatteryGroup) {
      const group = batteryGroups?.find((g) => g.id === selectedBatteryGroup);
      return group?.name || "Battery Group";
    }
    return "Select Battery or Battery Group";
  }, [selectedBattery, selectedBatteryGroup, batteries, batteryGroups]);

  // Map forecast data to table intervals
  const timeIntervals = useMemo(() => {
    if (!forecasts || forecasts.length === 0) {
      return [];
    }

    return forecasts.map((forecast) => {
      const eeTime = dayjs.utc(forecast.startTime).tz("Europe/Tallinn");
      const npTime = dayjs.utc(forecast.startTime).tz("Europe/Oslo");
      const dateStr = eeTime.format("DD-MM-YYYY");

      return {
        forecastId: forecast.id,
        startTime: forecast.startTime,
        date: dateStr,
        eeTime: eeTime.format("HH:mm"),
        npTime: npTime.format("HH:mm"),
        actualSoC: forecast.socReal,
        forecastedSoC: forecast.socForecast,
        chargeLevel: null, // Field removed from API
        productionForecast: null, // Not provided by API
        potentialSell: forecast.maxSellCapacity,
        potentialBuy: forecast.maxBuyCapacity,
        plannedTransaction: forecast.plannedTransaction,
        plannedTransactionPrice: forecast.plannedTransactionPrice,
        plannedTransactionIsExecuted: forecast.plannedTransactionIsExecuted,
        implementedManagement: forecast.executedTransaction,
        executedTransactionAvgPrice: forecast.executedTransactionAvgPrice,
        mfrrTransactions: forecast.mffrTransactions ?? [],
        change: forecast.change,
      };
    });
  }, [forecasts]);

  const handleSave = async () => {
    const changedIds = Object.keys(changes).map(Number);
    if (changedIds.length === 0) return;

    setSaving(true);
    setErrorFields({});
    try {
      const parseField = (value: string | undefined, fallback: number | null) => {
        if (value === undefined || value === "") return fallback;
        const parsed = parseFloat(value);
        return Number.isNaN(parsed) ? fallback : parsed;
      };

      const payloads = changedIds.map((forecastId) => {
        const forecast = forecasts.find((f) => f.id === forecastId);
        const change = changes[forecastId];

        return {
          forecastId,
          plannedTransaction: parseField(
            change.plannedTransaction,
            forecast?.plannedTransaction ?? null,
          ),
          plannedTransactionPrice: parseField(
            change.plannedTransactionPrice,
            forecast?.plannedTransactionPrice ?? null,
          ),
          plannedTransactionIsExecuted:
            change.plannedTransactionIsExecuted !== undefined
              ? change.plannedTransactionIsExecuted
              : forecast?.plannedTransactionIsExecuted ?? null,
        };
      });

      await savePlannedTransactions(payloads);

      // Silently refetch forecasts to get updated data from the server
      await fetchForecasts(true);

      setSnackbar({
        type: "success",
        message: "Planned transactions saved successfully",
      });
    } catch (error: any) {
      console.error("Error saving planned transactions:", error);
      const data = error.response?.data;
      const errors: Array<{ field?: string; message?: string }> = Array.isArray(
        data?.errors,
      )
        ? data.errors
        : [];

      let message = "Failed to save planned transactions";
      if (errors.length > 0) {
        const labels = Array.from(
          new Set(
            errors
              .map((e) => (e.field ? FIELD_LABELS[e.field] ?? e.field : null))
              .filter(Boolean) as string[],
          ),
        );
        const formatList = (items: string[]) => {
          if (items.length <= 1) return items.join("");
          if (items.length === 2) return `${items[0]} and ${items[1]}`;
          return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
        };
        const verb = labels.length > 1 ? "are" : "is";
        message =
          labels.length > 0
            ? `${formatList(labels)} ${verb} required`
            : errors.map((e) => e.message).filter(Boolean).join("; ");

        const newErrorFields: Record<number, Set<string>> = {};
        changedIds.forEach((forecastId) => {
          const forecast = forecasts.find((f) => f.id === forecastId);
          const change = changes[forecastId];
          errors.forEach((err) => {
            if (!err.field) return;
            const raw = (change as Record<string, string | undefined>)[
              err.field
            ];
            const fallback = (forecast as unknown as Record<string, number | null>)?.[
              err.field
            ];
            const finalValue =
              raw !== undefined && raw !== "" ? parseFloat(raw) : fallback;
            if (
              finalValue === null ||
              finalValue === undefined ||
              Number.isNaN(finalValue)
            ) {
              if (!newErrorFields[forecastId]) {
                newErrorFields[forecastId] = new Set();
              }
              newErrorFields[forecastId].add(err.field);
            }
          });
        });
        setErrorFields(newErrorFields);
      } else if (data?.message) {
        message = data.message;
      } else if (error.message) {
        message = error.message;
      }

      setSnackbar({
        type: "error",
        message,
      });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = Object.keys(changes).length > 0;

  return (
    <MainLayout
      snackbar={{
        open: !!snackbar,
        handleClose: snackbarClose,
        type: snackbar?.type,
        message: snackbar?.message || "",
      }}
    >
      <Typography
        variant="h5"
        sx={{ position: "relative", maxWidth: "fit-content", mb: 2 }}
      >
        Battery forecasts
      </Typography>

      <Box
        sx={{
          mb: 2,
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", gap: 2 }}>
          <FormControl
            sx={{
              minWidth: 280,
              maxWidth: 280,
              background: "white",
            }}
            size="small"
          >
            <DatePicker
              slotProps={{ textField: { size: "small" } }}
              label="Date"
              value={selectedDate}
              onChange={(value) => {
                setSelectedDate(value);
                updateUrlParams(value, selectedBattery, selectedBatteryGroup);
              }}
            />
          </FormControl>
          <Divider orientation="vertical" variant="middle" flexItem />
          <FormControl
            sx={{
              minWidth: 280,
              maxWidth: 280,
              background: "white",
            }}
            size="small"
          >
            <InputLabel id="battery-select-label">Battery</InputLabel>
            <Select
              labelId="battery-select-label"
              id="battery-select"
              value={selectedBattery || ""}
              label="Battery"
              onChange={(e) => {
                const newBatteryId = e.target.value as number;
                setSelectedBattery(newBatteryId);
                setSelectedBatteryGroup(null);
                updateUrlParams(selectedDate, newBatteryId, null);
              }}
            >
              {batteries && batteries.length > 0 ? (
                batteries.map((battery) => (
                  <MenuItem key={battery.id} value={battery.id}>
                    {battery.productionParkName +
                      " - " +
                      battery.id +
                      " (" +
                      battery.capacity +
                      "kWh)"}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No batteries available</MenuItem>
              )}
            </Select>
          </FormControl>
          <p
            style={{
              color: grey[600],
              fontSize: "14px",
              lineHeight: 1,
            }}
          >
            OR
          </p>
          <FormControl
            sx={{
              minWidth: 280,
              maxWidth: 280,
              background: "white",
            }}
            size="small"
          >
            <InputLabel id="battery-group-select-label">
              Battery Group
            </InputLabel>
            <Select
              labelId="battery-group-select-label"
              id="battery-group-select"
              value={selectedBatteryGroup || ""}
              label="Battery Group"
              onChange={(e) => {
                const newBatteryGroupId = e.target.value as number;
                setSelectedBatteryGroup(newBatteryGroupId);
                setSelectedBattery(null);
                updateUrlParams(selectedDate, null, newBatteryGroupId);
              }}
            >
              {batteryGroups && batteryGroups.length > 0 ? (
                batteryGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>No battery groups available</MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
        {!loading &&
          (selectedBattery || selectedBatteryGroup) &&
          timeIntervals.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={!hasChanges || saving}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          )}
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
            fontSize: "0.9rem",
            color: grey[600],
          }}
        >
          Loading forecasts...
        </Box>
      ) : !selectedBattery && !selectedBatteryGroup ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
            fontSize: "0.9rem",
            color: grey[600],
          }}
        >
          Please select a battery or battery group
        </Box>
      ) : timeIntervals.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
            fontSize: "0.9rem",
            color: grey[600],
          }}
        >
          No forecast data available for the selected date
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            boxShadow: 2,
            maxWidth: "100%",
            overflow: "hidden",
          }}
        >
          {/* Left and Middle Section */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
            }}
          >
            <Table
              size="small"
              component={Paper}
              sx={{ boxShadow: 0, borderCollapse: "separate" }}
            >
              <TableHead>
                {/* Section Title Row */}
                <TableRow>
                  {/* Empty cells for fixed date/time columns */}
                  <TableCell
                    colSpan={3}
                    sx={{
                      backgroundColor: grey[300],
                      fontWeight: "bold",
                      position: "sticky",
                      left: 0,
                      zIndex: 3,
                      borderRight: `2px solid ${grey[400]}`,
                    }}
                  />

                  {/* Battery section title */}
                  <TableCell
                    colSpan={14}
                    sx={{
                      backgroundColor: grey[300],
                      fontWeight: "bold",
                      fontSize: "1rem",
                      position: "sticky",
                      left: 290,
                    }}
                  >
                    {selectedDisplayName}
                  </TableCell>
                </TableRow>

                {/* Column Header Row */}
                <TableRow>
                  {/* Fixed date and time columns */}
                  <TableCell
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 110,
                      position: "sticky",
                      left: 0,
                      zIndex: 3,
                      borderRight: `1px solid ${grey[300]}`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Date
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 80,
                      position: "sticky",
                      left: 110,
                      zIndex: 3,
                      borderRight: `1px solid ${grey[300]}`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Time (EE)
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 80,
                      position: "sticky",
                      left: 205,
                      zIndex: 3,
                      borderRight: `2px solid ${grey[400]}`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Time (NP)
                  </TableCell>

                  {/* Battery data columns - scrollable middle section */}
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 90,
                    }}
                  >
                    Actual SoC (%)
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 90,
                    }}
                  >
                    Forecasted SoC (%)
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 100,
                    }}
                  >
                    Production forecast (kW)
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 100,
                    }}
                  >
                    Potential sell (kW)
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 100,
                    }}
                  >
                    Potential buy (kW)
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 80,
                    }}
                  >
                    Planned transaction (kW)
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 80,
                    }}
                  >
                    Planned price
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 60,
                    }}
                  >
                    Executed
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 80,
                    }}
                  >
                    Actual management
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 80,
                    }}
                  >
                    Actual price
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 80,
                    }}
                  >
                    Change (kWh)
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      width: 180,
                      minWidth: 180,
                      maxWidth: 180,
                    }}
                  >
                    mFRR transactions
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: grey[200],
                      fontWeight: "bold",
                      minWidth: 50,
                    }}
                  />
                </TableRow>
              </TableHead>
              <TableBody>
                {timeIntervals.map((interval, index) => {
                  const isOdd = index % 2 === 0;
                  const rowBgColor = isOdd ? grey[50] : "white";
                  const isPast = dayjs
                    .utc(interval.startTime)
                    .isBefore(dayjs());
                  const rowChange = changes[interval.forecastId];
                  const plannedTransactionValue =
                    rowChange?.plannedTransaction !== undefined
                      ? rowChange.plannedTransaction
                      : interval.plannedTransaction !== null
                        ? String(interval.plannedTransaction)
                        : "";
                  const plannedTransactionPriceValue =
                    rowChange?.plannedTransactionPrice !== undefined
                      ? rowChange.plannedTransactionPrice
                      : interval.plannedTransactionPrice !== null
                        ? interval.plannedTransactionPrice.toFixed(2)
                        : "";

                  return (
                    <TableRow
                      key={interval.forecastId}
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: grey[50] },
                        "&:hover": { backgroundColor: grey[100] },
                      }}
                    >
                      {/* Fixed date and time columns */}
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          backgroundColor: rowBgColor,
                          zIndex: 1,
                          fontWeight: "500",
                          borderRight: `1px solid ${grey[300]}`,
                          "&:hover": { backgroundColor: grey[100] },
                        }}
                      >
                        {interval.date}
                      </TableCell>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 110,
                          backgroundColor: rowBgColor,
                          zIndex: 1,
                          fontWeight: "500",
                          borderRight: `1px solid ${grey[300]}`,
                          "&:hover": { backgroundColor: grey[100] },
                        }}
                      >
                        {interval.eeTime}
                      </TableCell>
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 205,
                          backgroundColor: rowBgColor,
                          zIndex: 1,
                          fontWeight: "500",
                          borderRight: `2px solid ${grey[400]}`,
                          "&:hover": { backgroundColor: grey[100] },
                        }}
                      >
                        {interval.npTime}
                      </TableCell>

                      {/* Battery data columns - scrollable middle section */}
                      <TableCell align="right">
                        {interval.actualSoC !== null
                          ? (interval.actualSoC * 100).toFixed(0) + "%"
                          : "-"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          backgroundColor:
                            interval.forecastedSoC !== null &&
                            interval.forecastedSoC > 0.95
                              ? "error.main"
                              : "inherit",
                          color:
                            interval.forecastedSoC !== null &&
                            interval.forecastedSoC > 0.95
                              ? "white"
                              : "inherit",
                          fontWeight:
                            interval.forecastedSoC !== null &&
                            interval.forecastedSoC > 0.95
                              ? "bold"
                              : "normal",
                        }}
                      >
                        {interval.forecastedSoC !== null
                          ? (interval.forecastedSoC * 100).toFixed(0) + "%"
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {interval.productionForecast !== null
                          ? interval.productionForecast
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {interval.potentialSell !== null
                          ? interval.potentialSell
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {interval.potentialBuy !== null
                          ? interval.potentialBuy
                          : "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ padding: "0 16px" }}>
                        <input
                          type="number"
                          disabled={isPast}
                          value={plannedTransactionValue}
                          onChange={(e) =>
                            handleInputChange(
                              interval.forecastId,
                              "plannedTransaction",
                              e.target.value,
                            )
                          }
                          onWheel={(e) => e.currentTarget.blur()}
                          style={{
                            width: "100%",
                            maxWidth: "75px",
                            height: "100%",
                            border: errorFields[interval.forecastId]?.has(
                              "plannedTransaction",
                            )
                              ? "1px solid #d32f2f"
                              : 0,
                            padding: "0.3rem 0rem",
                            outline: "none",
                            backgroundColor: isPast
                              ? grey[200]
                              : errorFields[interval.forecastId]?.has(
                                    "plannedTransaction",
                                  )
                                ? "#fde7e7"
                                : "rgb(246 243 155)",
                            boxSizing: "border-box",
                            borderCollapse: "collapse",
                            textAlign: "right",
                            fontSize: "13px",
                            cursor: isPast ? "not-allowed" : "text",
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ padding: "0 16px" }}>
                        <input
                          type="number"
                          step="0.01"
                          disabled={isPast}
                          value={plannedTransactionPriceValue}
                          onChange={(e) =>
                            handleInputChange(
                              interval.forecastId,
                              "plannedTransactionPrice",
                              e.target.value,
                            )
                          }
                          onWheel={(e) => e.currentTarget.blur()}
                          style={{
                            width: "100%",
                            maxWidth: "75px",
                            height: "100%",
                            border: errorFields[interval.forecastId]?.has(
                              "plannedTransactionPrice",
                            )
                              ? "1px solid #d32f2f"
                              : 0,
                            padding: "0.3rem 0rem",
                            outline: "none",
                            backgroundColor: isPast
                              ? grey[200]
                              : errorFields[interval.forecastId]?.has(
                                    "plannedTransactionPrice",
                                  )
                                ? "#fde7e7"
                                : "rgb(246 243 155)",
                            boxSizing: "border-box",
                            borderCollapse: "collapse",
                            textAlign: "right",
                            fontSize: "13px",
                            cursor: isPast ? "not-allowed" : "text",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ padding: 0 }}>
                        <Checkbox
                          size="small"
                          disabled={isPast}
                          checked={
                            rowChange?.plannedTransactionIsExecuted !==
                            undefined
                              ? rowChange.plannedTransactionIsExecuted
                              : interval.plannedTransactionIsExecuted === true
                          }
                          onChange={(e) =>
                            handleIsExecutedChange(
                              interval.forecastId,
                              e.target.checked,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        {interval.implementedManagement !== null
                          ? interval.implementedManagement
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {interval.executedTransactionAvgPrice !== null &&
                        interval.executedTransactionAvgPrice !== undefined
                          ? interval.executedTransactionAvgPrice.toFixed(2)
                          : "-"}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color:
                            interval.change !== null && interval.change >= 0
                              ? "success.main"
                              : interval.change !== null
                                ? "error.main"
                                : "inherit",
                          fontWeight:
                            interval.change !== null ? "500" : "normal",
                        }}
                      >
                        {interval.change !== null ? (
                          <>
                            {interval.change >= 0 ? "+" : ""}
                            {interval.change}
                          </>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          fontSize: "12px",
                          verticalAlign: "top",
                          padding: "6px 16px",
                          width: 180,
                          minWidth: 180,
                          maxWidth: 180,
                        }}
                      >
                        {interval.mfrrTransactions &&
                        interval.mfrrTransactions.length > 0 ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.5,
                            }}
                          >
                            {interval.mfrrTransactions.map((tx, txIndex) => {
                              const mtuTime = tx.mtu
                                ? dayjs
                                    .utc(tx.mtu)
                                    .tz("Europe/Tallinn")
                                    .format("HH:mm")
                                : "-";
                              const delivered =
                                tx.deliveredKw !== null &&
                                tx.deliveredKw !== undefined
                                  ? tx.deliveredKw.toFixed(2)
                                  : "-";
                              const requested =
                                tx.requestedKw !== null &&
                                tx.requestedKw !== undefined
                                  ? tx.requestedKw.toFixed(0)
                                  : "-";
                              const price =
                                tx.price !== null && tx.price !== undefined
                                  ? tx.price.toFixed(2)
                                  : "-";
                              const statusColor =
                                (tx.status &&
                                  MFRR_STATUS_COLORS[tx.status]) ||
                                grey[400];
                              return (
                                <Box
                                  key={txIndex}
                                  sx={{
                                    lineHeight: 1.3,
                                    borderLeft: `3px solid ${statusColor}`,
                                    paddingLeft: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      fontWeight: 500,
                                      color: grey[800],
                                    }}
                                  >
                                    {mtuTime}
                                    {tx.market ? ` · ${tx.market}` : ""}
                                    {tx.status ? ` · ${tx.status}` : ""}
                                  </Box>
                                  <Box sx={{ color: grey[700] }}>
                                    {delivered} / {requested} kW · {price} €
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ padding: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setHistoryForecastId(interval.forecastId);
                            setHistoryTimeLabel(
                              `${interval.date.replace(/-/g, ".")} ${interval.eeTime}`,
                            );
                            setHistoryModal(true);
                          }}
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
      <Modal open={historyModal} onClose={() => setHistoryModal(false)}>
        <BatteryForecastHistoryModal
          forecastId={historyForecastId!}
          timeLabel={historyTimeLabel}
          setOpenModal={setHistoryModal}
        />
      </Modal>
    </MainLayout>
  );
};

export default Batteries;
